import BlockContent from '@sanity/block-content-to-react'
import imageUrlBuilder from '@sanity/image-url'
import { Col, Icon, Row } from 'antd'
import { darken } from 'polished'
import React, { Component, Fragment } from 'react'
import { Head, withRouteData, withSiteData } from 'react-static'
import styled, { withTheme } from 'styled-components'
import gameExclusiveFields from '../../sanity/schemas/games'
import ContentWrapper from '../components/ContentWrapper'
import Gallery from '../components/Gallery'
import HostOverview from '../components/HostOverview'
import PropItem from '../components/PropItem'
import Seperator from '../components/Seperator'
import { Divider } from '../styles/styled-common'
import { getControlPanelFromHost } from '../utils'
import { symbolByCode } from '../utils/currencies'
import getSerializers from '../utils/sanitySerializers'

const propColLayout = {
  xs: 24,
  md: 12,
  xl: 8,
  xxl: 6,
}

const CheckProp = ({ available, price, currSymbol }) =>
  available ? (
    <Fragment>
      <Icon type="check" />{' '}
      {Number.isFinite(price) &&
        (price === 0 ? 'Free' : currSymbol + price.toFixed(2))}
    </Fragment>
  ) : (
    <Icon type="close" />
  )

const DisclaimerBlock = styled.div`
  margin-top: 30px;
  font-size: 13px;
  p {
    margin: 0;
  }
`

const InterviewWrapper = styled(ContentWrapper)`
  color: ${p => p.theme.textGrey};
  h3 {
    color: #fff;
  }

  h3:not(:first-child) {
    margin-top: 2em;
  }

  figure {
    margin: 0;
    display: inline-block;
  }
`

const SectionHeading = styled.h2`
  color: ${p => p.theme.antLink};
`

const ScreenshotsGallery = styled(Gallery)`
  margin-top: -17px;
  a {
    width: 30%;
    margin: 20px 1% 0;
    border: 2px solid ${p => darken(0.15, p.theme.antLink)};
    background: ${p => darken(0.15, p.theme.antLink)};

    img {
      width: 100%;
      border-radius: 3px;
    }
  }
`

class Host extends Component {
  state = {}

  constructor(props) {
    super(props)

    const { marks, types } = getSerializers(props.siteData)

    this._serializers = {
      marks,
      types: {
        image: types.galleryImage,
      },
    }
  }

  static getDerivedStateFromProps(nextProps) {
    const Builder = imageUrlBuilder(
      Object.assign({}, nextProps.siteData.sanityConfig, {
        baseUrl: 'https://cdn.sanity.io',
      })
    )

    const controlPanel = getControlPanelFromHost(nextProps.host)

    return {
      logo: Builder.image(nextProps.host.logo).url(),
      controlPanel,
      controlPanelScreenshots: (controlPanel.screenshots || []).map(ss => ({
        src: Builder.image(ss).url(),
        thumbnail: Builder.image(ss)
          .fit('crop')
          .width(240)
          .height(140)
          .focalPoint(0, 0)
          .url(),
        thumbnailWidth: 240,
        thumbnailHeight: 140,
        srcset: [240, 360, 500, 750, 900, 1080, 1300, 1600].map(
          w =>
            `${Builder.image(ss)
              .fit('max')
              .width(w)
              .url()} ${w}w`
        ),
        thumbnailCaption: ss.caption,
        caption: ss.caption,
      })),
    }
  }

  render() {
    const {
      host,
      siteData: {
        site,
        textBlocks: { dataDisclaimer, interviewPrompt, noScreenshots },
        sanityConfig: { projectId, dataset },
      },
      theme,
    } = this.props

    const {
      name,
      ratings,
      id,
      baseCurrency,
      ddos,
      debranding,
      customerSupport,
      autoUpdates,
      autoBackups,
      locationSwitching,
      interview,
      ftpAccess,
      notes,
      gameInfo,
      gameInfo: { price, mods, platforms, slots, content },
    } = host

    const currSymbol = symbolByCode(baseCurrency)

    const exclusiveFields = gameExclusiveFields
      .find(f => f.sanityTypeDef.name === gameInfo._type)
      .fields.map(({ title, name }) => (
        <PropItem label={title} key={name}>
          <CheckProp
            available={gameInfo[name].available}
            price={gameInfo[name].price}
            currSymbol={currSymbol}
          />
        </PropItem>
      ))

    /*     let serverListSection = null

    if (serverList && serverList.serverListUrl) {
      serverListSection = <ServerList {...serverList} />
    }
    serverListSection = <ServerList {...serverList} /> */

    return (
      <Fragment>
        <Head>
          <meta
            name="description"
            content={`We gathered all the information about ${name} that you need to know before renting an ${
              site.game.shortName
            } server. Ratings, Reviews, Configuration options, Customer Support, Control Panel Screenshots, Mods, Interviews and more.`}
          />
          <title>{`${name} ${
            site.game.shortName
          } Server Hosting Information and Reviews - ${site.domain}`}</title>

          {/* SCHEMA  */}
          <script type="application/ld+json">
            {`{
              "@context": "http://schema.org/",
              "@type": "Product",
              "identifier": "${id}",
              "name": "${name} ${site.game.name} Server Hosting",
              "image":
                "${this.state.logo}",
              "offers": {
                "@type": "AggregateOffer",
                "availability": "http://schema.org/InStock",
                "lowPrice": ${(price.max * slots.min).toFixed(2)},
                "highPrice": ${(price.min * slots.max).toFixed(2)},
                "priceCurrency": "${baseCurrency}",
                "url": "/go/${id}"
              },
              "category": "${site.game.name} Server Hosting Rental",
              "aggregateRating": {
                "@type": "AggregateRating",
                "bestRating": 5,
                "worstRating": 0,
                "ratingValue": ${ratings.score},
                "ratingCount": ${ratings.reviewCount ||
                  Math.round(ratings.score * 7)}
                },
              "url": "/go/${id}"
              }`}
          </script>
        </Head>

        <ContentWrapper
          style={{ background: theme.offBlack, padding: '40px 0' }}
        >
          <HostOverview host={{ ...host, logo: this.state.logo }} />
        </ContentWrapper>
        <Seperator />
        <ContentWrapper style={{ background: theme.blue2, padding: '40px 0' }}>
          <SectionHeading>Details</SectionHeading>
          <Row gutter={{ xs: 0, sm: 0, md: 24, lg: 48, xl: 48, xxl: 48 }}>
            <Col {...propColLayout}>
              <h3>Configuration Options</h3>
              <PropItem label="Minimum Slots">{slots.min}</PropItem>
              <PropItem label="Maximum Slots">{slots.max}</PropItem>
              {price.min === price.max ? (
                <PropItem label="Price per slot">
                  {currSymbol}
                  {price.min.toFixed(2)}
                </PropItem>
              ) : (
                <Fragment>
                  <PropItem label={`Price per slot (${slots.min} slots)`}>
                    {currSymbol}
                    {price.max.toFixed(2)}
                  </PropItem>
                  <PropItem label={`Price per slot (${slots.max} slots)`}>
                    {currSymbol}
                    {price.min.toFixed(2)}
                  </PropItem>
                </Fragment>
              )}
              <PropItem label={`Price for ${slots.min} slots`}>
                {currSymbol}
                {(price.max * slots.min).toFixed(2)}
                /mo
              </PropItem>
              <PropItem label={`Price for ${slots.max} slots`}>
                {currSymbol}
                {(price.min * slots.max).toFixed(2)}
                /mo
              </PropItem>
              <Divider color={theme.textWhite} />
              <h3>Features</h3>
              <PropItem label="Supported Platforms">
                {platforms
                  .map(p =>
                    p === 'pc' ? 'PC' : p === 'playstation' ? 'PS4' : 'Xbox'
                  )
                  .join(', ')}
              </PropItem>
              <PropItem label="Control Panel Type">
                {this.state.controlPanel.type === 'tcadmin'
                  ? 'TCAdmin'
                  : 'Custom'}
              </PropItem>
              <PropItem label="DDoS Protection">
                <CheckProp {...ddos} currSymbol={currSymbol} />
              </PropItem>
              <PropItem label="Debranding">
                <Icon type="check" />{' '}
                {(debranding || 0) === 0
                  ? 'Free'
                  : currSymbol +
                    (debranding > 0 ? debranding : `${debranding} Discount`)}
              </PropItem>
              <Divider color={theme.textOffwhite} />
              {exclusiveFields.length > 0 && (
                <Fragment>
                  <h3>{site.game.name} Exclusive Features</h3>
                  {exclusiveFields}
                  <Divider color={theme.textOffwhite} />
                </Fragment>
              )}
              {notes && !!notes.length && (
                <Fragment>
                  <h3>Notes</h3>
                  {notes.map((n, i) => (
                    <div key={i}>{n}</div>
                  ))}
                </Fragment>
              )}
            </Col>
            <Col {...propColLayout}>
              <Col xs={24} md={0}>
                <Divider color={theme.textWhite} />
              </Col>
              {mods.supported && (
                <Fragment>
                  <h3>Mods</h3>
                  <PropItem label="Mod Support">
                    <CheckProp available={mods.supported} />
                  </PropItem>
                  <PropItem label="Mod Manager GUI">
                    <CheckProp available={mods.modManager} />
                  </PropItem>
                  <PropItem label="1-Click Mod Install">
                    <CheckProp available={mods.oneclick} />
                  </PropItem>
                  <PropItem label="Auto Updating Mods">
                    <CheckProp available={mods.autoUpdate} />
                  </PropItem>
                  <Divider color={theme.textWhite} />
                </Fragment>
              )}
              <h3>Customer Support</h3>
              <PropItem label="24/7">
                <CheckProp available={customerSupport.is247} />
              </PropItem>
              <PropItem label="Email">
                <CheckProp available={customerSupport.email} />
              </PropItem>
              <PropItem label="Ticket System">
                <CheckProp available={customerSupport.ticket} />
              </PropItem>
              <PropItem label="Chat">
                <CheckProp available={customerSupport.chat} />
              </PropItem>
              <Divider color={theme.textWhite} />
              <h3>Maintenance</h3>
              <PropItem label="Automatic Backups">
                <CheckProp available={autoBackups} />
              </PropItem>
              <PropItem label="Automatic Updates">
                <CheckProp available={autoUpdates} />
              </PropItem>
              <PropItem label="Location Switching">
                <CheckProp available={locationSwitching} />
              </PropItem>
              <PropItem label="FTP Access">
                <CheckProp available={ftpAccess} />
              </PropItem>
            </Col>
            <Col xs={24} xl={0}>
              <Divider color={theme.textOffwhite} />
            </Col>
            <Col xs={24} xl={8} xxl={12}>
              <h3>Control Panel Screenshots</h3>
              {this.state.controlPanelScreenshots.length === 0 ? (
                <BlockContent
                  blocks={noScreenshots.body}
                  serializers={this._serializers}
                />
              ) : (
                <ScreenshotsGallery
                  images={this.state.controlPanelScreenshots}
                />
              )}
            </Col>
          </Row>

          {/*           {serverListSection && (
            <Box flex="1 auto" ml={4}>
              {serverListSection}
            </Box>
          )} */}

          <DisclaimerBlock>
            <BlockContent
              blocks={dataDisclaimer.body}
              serializers={this._serializers}
            />
          </DisclaimerBlock>
        </ContentWrapper>
        <Seperator />
        {content ? (
          <InterviewWrapper
            layout={{
              xl: { span: 10, offset: 7 },
              xxl: { span: 10, offset: 7 },
            }}
            style={{ margin: '40px 0' }}
          >
            <BlockContent
              serializers={this._serializers}
              projectId={projectId}
              dataset={dataset}
              imageOptions={{
                thumbnail: { width: 320, fit: 'max' },
                full: { width: 1024, height: 768, fit: 'max' },
              }}
              blocks={content}
              isInline
            />
          </InterviewWrapper>
        ) : (
          interview && (
            <Fragment>
              <InterviewWrapper
                layout={{
                  xl: { span: 10, offset: 7 },
                  xxl: { span: 10, offset: 7 },
                }}
                style={{ margin: '40px 0' }}
              >
                <React.Fragment>
                  <div style={{ marginBottom: 40 }}>
                    <BlockContent
                      className="interview-prompt"
                      blocks={interviewPrompt.body}
                      serializers={this._serializers}
                    />
                  </div>
                  <SectionHeading>{name} Interview</SectionHeading>
                  <BlockContent
                    serializers={this._serializers}
                    projectId={projectId}
                    dataset={dataset}
                    imageOptions={{
                      thumbnail: { width: 320, fit: 'max' },
                      full: { width: 1024, height: 768, fit: 'max' },
                    }}
                    blocks={interview}
                    isInline
                  />
                </React.Fragment>
              </InterviewWrapper>
            </Fragment>
          )
        )}
        {(content || interview) && <Seperator />}
      </Fragment>
    )
  }
}

export default withSiteData(withRouteData(withTheme(Host)))



// WEBPACK FOOTER //
// /opt/build/repo/src/containers/Host.jsx